import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Close,
  Download,
  OpenInNew,
  NavigateBefore,
  NavigateNext,
  InsertDriveFileOutlined,
} from '@mui/icons-material';

const TEXT_TYPES = [
  'text/plain', 'text/csv', 'text/html', 'text/xml', 'text/css',
  'text/javascript', 'application/json', 'application/xml',
  'application/javascript', 'text/markdown',
];

const TEXT_EXTENSIONS = ['.txt', '.csv', '.log', '.json', '.xml', '.md', '.yml', '.yaml', '.ini', '.cfg', '.conf', '.env', '.sh', '.bat', '.py', '.js', '.jsx', '.ts', '.tsx', '.html', '.css', '.sql'];

function isTextFile(file) {
  if (TEXT_TYPES.some((t) => file.mime_type?.startsWith(t))) return true;
  const name = file.original_filename?.toLowerCase() || '';
  return TEXT_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function isImage(file) {
  return file.mime_type?.startsWith('image/');
}

function isPdf(file) {
  return file.mime_type === 'application/pdf';
}

function isVideo(file) {
  return file.mime_type?.startsWith('video/');
}

function isAudio(file) {
  return file.mime_type?.startsWith('audio/');
}

function hasPreview(file) {
  return isImage(file) || isPdf(file) || isVideo(file) || isAudio(file) || isTextFile(file);
}

function TextPreview({ url }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError(false);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.text();
      })
      .then((text) => setContent(text.slice(0, 100000)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [url]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
        Impossibile caricare il contenuto del file
      </Typography>
    );
  }

  return (
    <Box
      component="pre"
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.100',
        p: 2,
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: '70vh',
        fontSize: 13,
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        m: 0,
        lineHeight: 1.5,
      }}
    >
      {content}
    </Box>
  );
}

export default function FilePreviewDialog({ open, file, files, onClose, onDownload, onNavigate }) {
  const previewUrl = file?.qr_direct_url;
  const currentIndex = files?.findIndex((f) => f.id === file?.id) ?? -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (files?.length || 0) - 1;

  const goNext = useCallback(() => {
    if (hasNext && onNavigate) onNavigate(files[currentIndex + 1]);
  }, [hasNext, onNavigate, files, currentIndex]);

  const goPrev = useCallback(() => {
    if (hasPrev && onNavigate) onNavigate(files[currentIndex - 1]);
  }, [hasPrev, onNavigate, files, currentIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, goNext, goPrev]);

  if (!file) return null;

  const handleOpenNewTab = () => {
    if (previewUrl) window.open(previewUrl, '_blank');
  };

  const renderPreview = () => {
    if (isImage(file) && previewUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1,
            overflow: 'hidden',
            minHeight: 200,
          }}
        >
          <Tooltip title="Clicca per aprire a schermo intero">
            <Box
              component="img"
              src={previewUrl}
              alt={file.original_filename}
              onClick={handleOpenNewTab}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                cursor: 'pointer',
                display: 'block',
              }}
            />
          </Tooltip>
        </Box>
      );
    }

    if (isPdf(file) && previewUrl) {
      return (
        <Box
          component="iframe"
          src={previewUrl}
          title={file.original_filename}
          sx={{
            width: '100%',
            height: '70vh',
            border: 'none',
            borderRadius: 1,
            display: 'block',
          }}
        />
      );
    }

    if (isVideo(file) && previewUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'grey.900',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            component="video"
            controls
            autoPlay={false}
            src={previewUrl}
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              display: 'block',
            }}
          />
        </Box>
      );
    }

    if (isAudio(file) && previewUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            py: 4,
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <InsertDriveFileOutlined sx={{ fontSize: 64, color: 'primary.main' }} />
          <Typography variant="body1" fontWeight={500}>
            {file.original_filename}
          </Typography>
          <Box
            component="audio"
            controls
            src={previewUrl}
            sx={{ width: '100%', maxWidth: 500 }}
          />
        </Box>
      );
    }

    if (isTextFile(file) && previewUrl) {
      return <TextPreview url={previewUrl} />;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          gap: 2,
        }}
      >
        <InsertDriveFileOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Anteprima non disponibile per questo tipo di file
        </Typography>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() => onDownload && onDownload(file)}
          sx={{ textTransform: 'none', boxShadow: 'none' }}
        >
          Scarica il file
        </Button>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 1,
          gap: 1,
        }}
      >
        {/* Prev/Next navigation */}
        <Tooltip title="Precedente (freccia sinistra)">
          <span>
            <IconButton onClick={goPrev} disabled={!hasPrev} size="small">
              <NavigateBefore />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Successivo (freccia destra)">
          <span>
            <IconButton onClick={goNext} disabled={!hasNext} size="small">
              <NavigateNext />
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {file.original_filename}
        </Typography>

        {files && currentIndex >= 0 && (
          <Chip
            label={`${currentIndex + 1} / ${files.length}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 12 }}
          />
        )}

        <Tooltip title="Chiudi (ESC)">
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {renderPreview()}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {file.mime_type}
        </Typography>
        <Button
          startIcon={<Download />}
          onClick={() => onDownload && onDownload(file)}
          sx={{ textTransform: 'none' }}
        >
          Scarica
        </Button>
        <Button
          variant="contained"
          startIcon={<OpenInNew />}
          onClick={handleOpenNewTab}
          sx={{ textTransform: 'none', boxShadow: 'none' }}
          disabled={!previewUrl}
        >
          Apri in nuova scheda
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { hasPreview };
